import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";

type CourseGenStatus = "inprogress" | "completed" | "failed";

interface CourseGenerationState {
  courseId: string | null;
  status: CourseGenStatus | null;
  error: string;
  visible: boolean;
}

interface StartTrackingOptions {
  onComplete?: () => void;
  onFail?: (error: string) => void;
}

interface CourseGenerationContextValue extends CourseGenerationState {
  startTracking: (courseId: string, options?: StartTrackingOptions) => void;
  dismiss: () => void;
}

const STORAGE_KEY = "active_course_generation_id";
const POLL_INTERVAL_MS = 5000;
const AUTO_HIDE_MS = 90000; // how long the success banner stays before auto-dismissing
const DONE_STATUSES = ["completed", "ready", "generated"];
const FAILED_STATUSES = ["failed", "error"];

const CourseGenerationContext = createContext<CourseGenerationContextValue | undefined>(undefined);

export const CourseGenerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CourseGenerationState>({
    courseId: null,
    status: null,
    error: "",
    visible: false,
  });

  // Callbacks for whichever course is currently being tracked. Kept in a ref
  // so the poll loop (a plain recursive setTimeout, not a subscribed effect)
  // doesn't need to be redeclared every time this changes.
  const callbacksRef = useRef<StartTrackingOptions>({});
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  };

  const checkStatus = useCallback(async (courseId: string) => {
    const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
    try {
      const res = await axios.post(
        `${apiUrl}/api/prompts/course_status`,
        null,
        { params: { course_id: courseId }, headers: { accept: "application/json" } }
      );
      const { status, error } = res.data || {};

      if (DONE_STATUSES.includes(status)) {
        setState({ courseId, status: "completed", error: "", visible: true });
        localStorage.removeItem(STORAGE_KEY);
        callbacksRef.current.onComplete?.();
        // Auto-dismiss the success banner after AUTO_HIDE_MS, unless the
        // user has already closed it (dismiss() clears this same timer).
        autoHideTimeoutRef.current = setTimeout(() => {
          setState((prev) => (prev.courseId === courseId ? { ...prev, visible: false } : prev));
        }, AUTO_HIDE_MS);
        return;
      }

      if (FAILED_STATUSES.includes(status)) {
        setState({ courseId, status: "failed", error: error || "Course generation failed.", visible: true });
        localStorage.removeItem(STORAGE_KEY);
        callbacksRef.current.onFail?.(error || "");
        return; // stop polling — no auto-dismiss for errors, user must close it
      }

      // status is "inprogress"/"initiated"/etc — keep polling
      setState({ courseId, status: "inprogress", error: "", visible: true });
      pollTimeoutRef.current = setTimeout(() => checkStatus(courseId), POLL_INTERVAL_MS);
    } catch (err) {
      console.error("[CourseGenerationContext] status check failed:", err);
      pollTimeoutRef.current = setTimeout(() => checkStatus(courseId), POLL_INTERVAL_MS);
    }
  }, []);

  const startTracking = useCallback((courseId: string, options?: StartTrackingOptions) => {
    if (!courseId) return;
    clearTimers();
    callbacksRef.current = options || {};
    localStorage.setItem(STORAGE_KEY, courseId);
    setState({ courseId, status: "inprogress", error: "", visible: true });
    checkStatus(courseId);
  }, [checkStatus]);

  const dismiss = useCallback(() => {
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
    setState((prev) => ({ ...prev, visible: false }));
  }, []);

  // Resume tracking on app load / page refresh if a generation was left running.
  useEffect(() => {
    const storedCourseId = localStorage.getItem(STORAGE_KEY);
    if (storedCourseId) {
      setState({ courseId: storedCourseId, status: "inprogress", error: "", visible: true });
      checkStatus(storedCourseId);
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CourseGenerationContext.Provider value={{ ...state, startTracking, dismiss }}>
      {children}
    </CourseGenerationContext.Provider>
  );
};

export const useCourseGeneration = () => {
  const ctx = useContext(CourseGenerationContext);
  if (!ctx) {
    throw new Error("useCourseGeneration must be used within a CourseGenerationProvider");
  }
  return ctx;
};